"use client";

// src/context/MenuContext.js

import { createContext, useContext, useState } from "react";

const MenuContext = createContext();

export const useMenu = () => useContext(MenuContext);

export function MenuProvider({ children }) {
  // ============================================================
  // CART & WISHLIST COUNTS
  // ============================================================

  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  // ============================================================
  // COUPON / PROMO STATE
  // ============================================================

  const [promoCode, setPromoCode] = useState("");
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [promoMessage, setPromoMessage] = useState("");

  // ============================================================
  // SHIPPING
  // ============================================================

  const [shippingCharge, setShippingCharge] = useState(0);
  const [shippingError, setShippingError] = useState(null);

  // ============================================================
  // MENU DATA
  // ============================================================

  const [menuItems, setMenuItems] = useState([]);
  const [menuSettings, setSettingsItems] = useState([]);
  const [selectedMain, setSelectedMain] = useState(null);
  const [selectedSub, setSelectedSub] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);

  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState(null);

  const [logoUrl, setLogoUrl] = useState(null);

  // ============================================================
  // USER
  // ============================================================

  let user = null;

  try {
    user = JSON.parse(localStorage.getItem("authUserId"));
  } catch (error) {
    user = null;
  }

  const id = user;

  // ============================================================
  // GET AUTH / GUEST DATA
  // ============================================================

  const getAuthData = () => {
    return {
      authToken: localStorage.getItem("authToken") || null,
      guestToken: localStorage.getItem("guest_token") || null,
      userId: localStorage.getItem("authUserId") || null,
    };
  };

  // ============================================================
  // FETCH MENU ITEMS
  // ============================================================

  const fetchMenuItems = async () => {
    const authData = getAuthData();

    const token = authData.authToken || authData.guestToken;

    setMenuLoading(true);

    try {
      const res = await fetch(
        `https://tyka.premierhostings.com/beta/api/menu${
          id ? `?id=${id}` : ""
        }`,
        {
          headers: {
            "Content-Type": "application/json",

            ...(token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {}),
          },
        },
      );

      if (!res.ok) {
        throw new Error(`Failed to fetch menu, status: ${res.status}`);
      }

      const data = await res.json();

      // ========================================================
      // API MENU
      // ========================================================

      const items = Array.isArray(data.data) ? data.data : [];

      // ========================================================
      // NEW ARRIVALS
      // Separate TOP LEVEL menu item
      // ========================================================

      const newArrivals = {
        id: "new-arrivals",
        name: "New Arrivals",
        slug: "new-arrivals",
        subcategories: [],
      };

      // ========================================================
      // CREATE UPDATED MENU
      // New Arrivals should ALWAYS be first
      // ========================================================

      const updatedItems = [newArrivals, ...items];

      // ========================================================
      // SETTINGS / LOGO
      // ========================================================

      if (data.settings?.logo) {
        setSettingsItems(data.settings);
        setLogoUrl(data.settings.logo);
      }

      // ========================================================
      // SAVE MENU
      // ========================================================

      setMenuError(null);

      setMenuItems(updatedItems);

      // ========================================================
      // DEFAULT MENU SELECTION
      // ========================================================

      if (updatedItems.length) {
        const main = updatedItems[0];

        const sub = main?.subcategories?.[0] || null;

        const child = sub?.children?.[0] || null;

        setSelectedMain(main);
        setSelectedSub(sub);
        setSelectedChild(child);
      } else {
        setSelectedMain(null);
        setSelectedSub(null);
        setSelectedChild(null);
      }

      // ========================================================
      // WISHLIST COUNT
      // ========================================================

      setWishlistCount(data.wishlist || 0);

      // ========================================================
      // CART COUNT
      // ========================================================

      if (id) {
        setCartCount(typeof data.cart === "number" ? data.cart : 0);
      }
    } catch (err) {
      console.error("Menu fetch error:", err);

      setMenuError(err.message);
    } finally {
      setMenuLoading(false);
    }
  };

  // ============================================================
  // SELECT MAIN CATEGORY
  // ============================================================

  const selectMain = (main) => {
    setSelectedMain(main);

    const sub = main?.subcategories?.[0] || null;

    const child = sub?.children?.[0] || null;

    setSelectedSub(sub);
    setSelectedChild(child);
  };

  // ============================================================
  // SELECT SUB CATEGORY
  // ============================================================

  const selectSub = (sub) => {
    setSelectedSub(sub);

    const child = sub?.children?.[0] || null;

    setSelectedChild(child);
  };

  // ============================================================
  // SELECT CHILD CATEGORY
  // ============================================================

  const selectChild = (child) => {
    setSelectedChild(child);
  };

  // ============================================================
  // FETCH SHIPPING CHARGE
  // ============================================================

  const fetchShippingCharge = async () => {
    try {
      const res = await fetch(
        "https://tyka.premierhostings.com/beta/api/shipping-charge",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      const charge = parseFloat(data.shipping_charge);

      if (!isNaN(charge)) {
        setShippingCharge(charge);
        setShippingError(null);
      } else {
        throw new Error("Invalid shipping charge from API");
      }
    } catch (err) {
      console.error("Shipping charge fetch error:", err);

      setShippingError(err.message);
      setShippingCharge(0);
    }
  };

  // ============================================================
  // FETCH CART & WISHLIST COUNTS
  // ============================================================

  const fetchMenuCounts = async (userId, token) => {
    try {
      const res = await fetch(
        `https://tyka.premierhostings.com/beta/api/menu${
          userId ? `?id=${userId}` : ""
        }`,
        {
          headers: {
            "Content-Type": "application/json",

            ...(token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {}),
          },
        },
      );

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();

      // Wishlist
      setWishlistCount(data.wishlist || 0);

      // Cart
      if (userId) {
        setCartCount(typeof data.cart === "number" ? data.cart : 0);
      }
    } catch (err) {
      console.error("Menu count fetch error:", err);
    }
  };

  // ============================================================
  // CONTEXT PROVIDER
  // ============================================================

  return (
    <MenuContext.Provider
      value={{
        // ======================================================
        // CART & WISHLIST
        // ======================================================

        cartCount,
        wishlistCount,

        setCartCount,
        setWishlistCount,

        fetchMenuCounts,

        // ======================================================
        // COUPON
        // ======================================================

        promoCode,
        setPromoCode,

        promoDiscount,
        setPromoDiscount,

        finalAmount,
        setFinalAmount,

        promoMessage,
        setPromoMessage,

        // ======================================================
        // SHIPPING
        // ======================================================

        shippingCharge,
        setShippingCharge,

        fetchShippingCharge,

        shippingError,

        // ======================================================
        // MENU
        // ======================================================

        menuItems,

        fetchMenuItems,

        menuSettings,

        logoUrl,

        selectedMain,
        selectedSub,
        selectedChild,

        selectMain,
        selectSub,
        selectChild,

        menuLoading,
        menuError,
      }}
    >
      {children}
    </MenuContext.Provider>
  );
}
